import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Snackbar,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Group as GroupIcon,
  Paid as PaidIcon,
  Bolt as BoltIcon,
  WarningAmber as WarningIcon,
  PeopleAlt as PeopleAltIcon,
  Savings as SavingsIcon,
  AssignmentInd as AssignmentIndIcon,
  LocalAtm as LocalAtmIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import api from '../../services/api';
import RechargeRecordPanel from './RechargeRecordPanel';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

const DashboardWrapper = styled(Box)(({ theme }) => ({
  padding: { xs: theme.spacing(1.5), sm: theme.spacing(2), md: theme.spacing(3) },
  background: 'radial-gradient(circle at top, rgba(0, 60, 120, 0.6), rgba(0, 20, 40, 0.95))',
  minHeight: '100vh',
  color: '#f5f7fa'
}));

const SectionCard = styled(Card)(({ theme }) => ({
  borderRadius: 18,
  background: 'rgba(6, 21, 44, 0.72)',
  border: '1px solid rgba(0, 229, 255, 0.14)',
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.25)',
  backdropFilter: 'blur(18px)',
  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
  overflow: 'hidden',
  [theme.breakpoints.up('md')]: {
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 28px 45px rgba(0, 0, 0, 0.28)'
    }
  }
}));

const SummaryChip = styled(Chip)(({ theme }) => ({
  color: '#c7f8ff',
  backgroundColor: 'rgba(0, 229, 255, 0.18)',
  borderRadius: 12,
  fontWeight: 600,
  letterSpacing: 0.4
}));

const SummaryCard = styled(SectionCard)(({ theme }) => ({
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  height: '100%'
}));

const numberFormatter = new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 0 });
const decimalFormatter = new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 2 });

const formatNumber = (value, digits = 0) => {
  if (value === null || value === undefined) return '-';
  if (Math.abs(value) >= 10000) {
    return `${decimalFormatter.format(value / 10000)} 万`;
  }
  if (digits === 0) {
    return numberFormatter.format(value);
  }
  return new Intl.NumberFormat('zh-CN', { maximumFractionDigits: digits }).format(value);
};

const timeRanges = [
  { label: '7天', value: 7 },
  { label: '14天', value: 14 },
  { label: '30天', value: 30 },
  { label: '90天', value: 90 }
];

const AdminAnalyticsDashboard = () => {
  const [timeRange, setTimeRange] = useState(7);
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState(null);
  const [segments, setSegments] = useState(null);
  const [topUsers, setTopUsers] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const fetchAnalytics = useCallback(async (range = timeRange) => {
    setLoading(true);
    try {
      const [overviewRes, trendsRes, segmentsRes, topUsersRes] = await Promise.all([
        api.getAdminDashboardOverview(range),
        api.getAdminDashboardTrends(range),
        api.getAdminUserSegments(),
        api.getAdminTopUsers(5)
      ]);

      const overviewData = overviewRes?.data || overviewRes;
      const trendsData = trendsRes?.data || trendsRes;
      const segmentsData = segmentsRes?.data || segmentsRes;
      const topUsersData = topUsersRes?.data || topUsersRes;

      setOverview(overviewData);
      setTrends(trendsData);
      setSegments(segmentsData);
      setTopUsers(topUsersData);
    } catch (error) {
      console.error('[AdminAnalytics] 数据获取失败:', error);
      setSnackbar({
        open: true,
        message: error?.userMessage || error?.message || '获取数据失败，请稍后重试',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchAnalytics(timeRange);
  }, [timeRange, fetchAnalytics]);

  const handleRefresh = () => {
    fetchAnalytics(timeRange);
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const summaryItems = useMemo(() => {
    if (!overview) return [];
    const { summary, alerts } = overview;
    return [
      {
        title: '总用户',
        value: summary.totalUsers,
        caption: `付费占比 ${summary.payingUserRate || '0'}%`,
        icon: <GroupIcon color="primary" />,
        accent: 'rgba(0, 229, 255, 0.25)'
      },
      {
        title: '7日活跃',
        value: summary.activeUsers7d,
        caption: `活跃率 ${summary.activeRates?.sevenDay || '0'}%`,
        icon: <BoltIcon color="secondary" />,
        accent: 'rgba(118, 255, 3, 0.18)'
      },
      {
        title: '新增用户',
        value: summary.newUsersInPeriod,
        caption: `最近 ${timeRange} 天`,
        icon: <PeopleAltIcon sx={{ color: '#ffd600' }} />,
        accent: 'rgba(255, 214, 0, 0.22)'
      },
      {
        title: '充值金额',
        value: summary.recharge.totalAmount,
        caption: `订单 ${summary.recharge.orderCount} 笔`,
        icon: <PaidIcon sx={{ color: '#4dd0e1' }} />,
        accent: 'rgba(77, 208, 225, 0.24)'
      },
      {
        title: '消费字数',
        value: summary.consumption.totalAmount,
        caption: `共 ${summary.consumption.orderCount} 次`,
        icon: <TrendingUpIcon sx={{ color: '#ff80ab' }} />,
        accent: 'rgba(255, 128, 171, 0.24)'
      },
      {
        title: '余额池',
        value: summary.totalBalance,
        caption: `人均 ${formatNumber(summary.averageBalance, 2)}`,
        icon: <SavingsIcon sx={{ color: '#80d8ff' }} />,
        accent: 'rgba(128, 216, 255, 0.18)'
      },
      {
        title: '低余额用户',
        value: alerts.lowBalanceUsers,
        caption: '≤ 1000 字',
        icon: <WarningIcon sx={{ color: '#ffab40' }} />,
        accent: 'rgba(255, 171, 64, 0.22)'
      },
      {
        title: '90日沉睡',
        value: alerts.dormantUsers,
        caption: `占比 ${alerts.inactiveRate90d}%`,
        icon: <AssignmentIndIcon sx={{ color: '#7c4dff' }} />,
        accent: 'rgba(124, 77, 255, 0.18)'
      }
    ];
  }, [overview, timeRange]);

  const trendCharts = useMemo(() => {
    if (!trends) return { users: null, recharge: null };

    const labels = trends.labels || [];

    const usersChart = {
      labels,
      datasets: [
        {
          label: '新增用户',
          data: trends.newUsers || [],
          borderColor: '#00e5ff',
          backgroundColor: 'rgba(0, 229, 255, 0.18)',
          tension: 0.35,
          fill: true,
          pointRadius: 3,
          pointHoverRadius: 6
        },
        {
          label: '活跃用户',
          data: trends.activeUsers || [],
          borderColor: '#76ff03',
          backgroundColor: 'rgba(118, 255, 3, 0.16)',
          tension: 0.35,
          fill: true,
          pointRadius: 3,
          pointHoverRadius: 6
        }
      ]
    };

    const revenueChart = {
      labels,
      datasets: [
        {
          label: '充值金额',
          data: (trends.rechargeAmount || []).map((amount) => amount || 0),
          backgroundColor: 'rgba(0, 229, 255, 0.6)',
          borderRadius: 8,
          maxBarThickness: 24
        },
        {
          label: '消费字数',
          data: (trends.consumptionAmount || []).map((amount) => amount || 0),
          backgroundColor: 'rgba(118, 255, 3, 0.45)',
          borderRadius: 8,
          maxBarThickness: 24
        }
      ]
    };

    return { users: usersChart, revenue: revenueChart };
  }, [trends]);

  const distributionCharts = useMemo(() => {
    if (!segments) return null;
    const balanceLabels = (segments.balanceDistribution || []).map((item) => item.label);
    const balanceData = (segments.balanceDistribution || []).map((item) => item.count);

    const totalBalanceUsers = balanceData.reduce((sum, count) => sum + count, 0);

    return {
      balance: {
        labels: balanceLabels,
        datasets: [
          {
            label: '人数',
            data: balanceData,
            backgroundColor: [
              'rgba(255, 82, 82, 0.75)',
              'rgba(255, 160, 0, 0.75)',
              'rgba(0, 229, 255, 0.75)',
              'rgba(118, 255, 3, 0.75)',
              'rgba(0, 191, 165, 0.75)',
              'rgba(171, 71, 188, 0.75)'
            ],
            borderWidth: 1
          }
        ],
        total: totalBalanceUsers
      }
    };
  }, [segments]);

  const chartOptions = useMemo(() => ({
    line: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { intersect: false, mode: 'index' },
      plugins: {
        legend: {
          position: 'top',
          labels: {
            color: 'rgba(228, 241, 255, 0.85)',
            font: { size: 12 }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(4, 20, 44, 0.92)',
          borderColor: 'rgba(0, 229, 255, 0.35)',
          borderWidth: 1,
          titleColor: '#fff',
          bodyColor: '#fff',
          padding: 12
        }
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: 'rgba(214, 227, 252, 0.6)' }
        },
        y: {
          grid: { color: 'rgba(255, 255, 255, 0.08)' },
          ticks: { color: 'rgba(214, 227, 252, 0.6)' },
          beginAtZero: true
        }
      }
    },
    bar: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            color: 'rgba(228, 241, 255, 0.85)',
            font: { size: 12 }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(4, 20, 44, 0.92)',
          borderColor: 'rgba(0, 229, 255, 0.35)',
          borderWidth: 1,
          titleColor: '#fff',
          bodyColor: '#fff',
          padding: 12
        }
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: 'rgba(214, 227, 252, 0.6)' }
        },
        y: {
          grid: { color: 'rgba(255, 255, 255, 0.08)' },
          ticks: { color: 'rgba(214, 227, 252, 0.6)' },
          beginAtZero: true
        }
      }
    },
    doughnut: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: 'rgba(228, 241, 252, 0.85)',
            font: { size: 11 },
            boxWidth: 12,
            padding: 6
          }
        },
        tooltip: {
          backgroundColor: 'rgba(4, 20, 44, 0.92)',
          borderColor: 'rgba(0, 229, 255, 0.35)',
          borderWidth: 1,
          titleColor: '#fff',
          bodyColor: '#fff',
          padding: 12,
          callbacks: {
            label: (context) => {
              const label = context.label || '';
              const value = context.parsed;
              const total = context.dataset.data.reduce((sum, current) => sum + current, 0);
              const percent = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
              return `${label}: ${value} (${percent}%)`;
            }
          }
        }
      }
    }
  }), []);

  const renderTopList = (list = [], emptyText) => (
    <List dense>
      {list.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {emptyText}
        </Typography>
      ) : (
        list.map((item, index) => (
          <ListItem key={item.userId || item._id || index} sx={{ px: 0 }}>
            <ListItemAvatar>
              <Avatar sx={{ bgcolor: 'rgba(0, 229, 255, 0.2)', color: '#00e5ff' }}>
                {index + 1}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" color="#e4f1ff">
                    {item.username || '未知用户'}
                  </Typography>
                  <Typography variant="subtitle2" color="#80d8ff">
                    {formatNumber(item.totalRecharge || item.totalConsumption || item.balance)}
                  </Typography>
                </Stack>
              }
              secondary={
                <Stack direction="row" spacing={1} alignItems="center">
                  {item.role && <SummaryChip label={item.role === 'admin' ? '管理员' : '普通用户'} size="small" />}
                  {item.rechargeCount && (
                    <Typography variant="caption" color="rgba(214, 227, 252, 0.65)">
                      订单 {item.rechargeCount}
                    </Typography>
                  )}
                  {item.lastRechargeAt && (
                    <Typography variant="caption" color="rgba(214, 227, 252, 0.45)">
                      {new Date(item.lastRechargeAt).toLocaleDateString()}
                    </Typography>
                  )}
                </Stack>
              }
            />
          </ListItem>
        ))
      )}
    </List>
  );

  return (
    <DashboardWrapper>
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#e4f1ff', fontSize: { xs: 'h5', md: 'h4' } }}>
              管理员数据驾驶舱
            </Typography>
            <Typography variant="subtitle2" color="rgba(214, 227, 252, 0.65)" sx={{ mt: 0.5, fontSize: { xs: '0.7rem', md: '0.875rem' } }}>
              全局用户活跃、营收与风险概览 · 实时统计（包含今日数据）
            </Typography>
          </Box>
            <Button
              variant="contained"
              color="primary"
              onClick={handleRefresh}
              startIcon={<RefreshIcon />}
              disabled={loading}
              sx={{ borderRadius: 2, alignSelf: { xs: 'stretch', md: 'auto' } }}
            >
              {loading ? '更新中...' : '刷新数据'}
            </Button>
        </Stack>

        {/* 充值记录面板（置顶） */}
        <RechargeRecordPanel timeRange={timeRange} onTimeRangeChange={setTimeRange} />

        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={220}>
            <CircularProgress color="primary" />
          </Box>
        )}

        {!loading && overview && (
          <>
            <Grid container spacing={2}>
              {summaryItems.map((item) => (
                <Grid item xs={12} sm={6} md={3} key={item.title}>
                  <SummaryCard>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2" sx={{ color: 'rgba(214,227,252,0.75)' }}>
                        {item.title}
                      </Typography>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: '50%',
                          background: item.accent,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {item.icon}
                      </Box>
                    </Stack>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#f5f7fa' }}>
                      {formatNumber(item.value)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(214,227,252,0.55)' }}>
                      {item.caption}
                    </Typography>
                  </SummaryCard>
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                <SectionCard>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Typography variant="h6" color="#e4f1ff" fontWeight={600}>
                        用户增长与活跃趋势
                      </Typography>
                      <SummaryChip label="每日更新" size="small" />
                    </Stack>
                    <Box sx={{ height: 280 }}>
                      {trendCharts.users ? (
                        <Line data={trendCharts.users} options={chartOptions.line} />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          暂无趋势数据
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </SectionCard>
              </Grid>
              <Grid item xs={12} md={5}>
                <SectionCard>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Typography variant="h6" color="#e4f1ff" fontWeight={600}>
                        充值与消耗对比
                      </Typography>
                      <SummaryChip label="金额单位：字数" size="small" />
                    </Stack>
                    <Box sx={{ height: 280 }}>
                      {trendCharts.revenue ? (
                        <Bar data={trendCharts.revenue} options={chartOptions.bar} />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          暂无账单数据
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </SectionCard>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} md={5}>
                <SectionCard>
                  <CardContent>
                    <Typography variant="h6" color="#e4f1ff" fontWeight={600}>
                      用户余额分布
                    </Typography>
                    <Typography variant="caption" color="rgba(214,227,252,0.55)">
                      快速定位余额梯度情况
                    </Typography>
                    <Box sx={{ height: 260, mt: 2 }}>
                      {distributionCharts?.balance ? (
                        <Doughnut data={distributionCharts.balance} options={chartOptions.doughnut} />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          暂无余额分布数据
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </SectionCard>
              </Grid>

              <Grid item xs={12} md={7}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <SectionCard>
                      <CardContent>
                        <Typography variant="h6" color="#e4f1ff" fontWeight={600} sx={{ mb: 1 }}>
                          用户角色与状态
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="subtitle2" color="rgba(214,227,252,0.65)" gutterBottom>
                              角色分布
                            </Typography>
                            <Stack spacing={1.5}>
                              {(segments?.roleDistribution || []).map((item) => (
                                <Stack key={item.label} direction="row" justifyContent="space-between">
                                  <Typography variant="body2" color="#cfd8ff">
                                    {item.label === 'admin' ? '管理员' : '普通用户'}
                                  </Typography>
                                  <Typography variant="body2" color="#80d8ff">
                                    {item.count}
                                  </Typography>
                                </Stack>
                              ))}
                            </Stack>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="subtitle2" color="rgba(214,227,252,0.65)" gutterBottom>
                              状态分布
                            </Typography>
                            <Stack spacing={1.5}>
                              {(segments?.statusDistribution || []).map((item) => (
                                <Stack key={item.label} direction="row" justifyContent="space-between">
                                  <Typography variant="body2" color="#cfd8ff">
                                    {item.label === 'active' ? '正常' : item.label === 'inactive' ? '禁用' : item.label}
                                  </Typography>
                                  <Typography variant="body2" color="#80d8ff">
                                    {item.count}
                                  </Typography>
                                </Stack>
                              ))}
                            </Stack>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </SectionCard>
                  </Grid>
                  <Grid item xs={12}>
                    <SectionCard>
                      <CardContent>
                        <Typography variant="h6" color="#e4f1ff" fontWeight={600} sx={{ mb: 1 }}>
                          内容访问权限
                        </Typography>
                        <Stack spacing={1.5}>
                          {(segments?.accessStatusDistribution || []).map((item) => (
                            <Stack key={item.label} direction="row" justifyContent="space-between">
                              <Typography variant="body2" color="#cfd8ff">
                                {item.label === 'unrestricted' ? '无限制' : 
                                 item.label === 'fullAccess' ? '永久通行' : 
                                 item.label === 'unknown' ? '未知' : 
                                 item.label === 'trialOnly' ? '仅试用' : item.label}
                              </Typography>
                              <Typography variant="body2" color="#80d8ff">
                                {item.count}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </CardContent>
                    </SectionCard>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <SectionCard>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6" color="#e4f1ff" fontWeight={600}>
                        充值榜
                      </Typography>
                      <SummaryChip label="金额单位：字数" size="small" />
                    </Stack>
                    <Divider sx={{ my: 2, borderColor: 'rgba(0, 229, 255, 0.12)' }} />
                    {renderTopList(topUsers?.topRechargeUsers, '近期暂无充值记录')}
                  </CardContent>
                </SectionCard>
              </Grid>
              <Grid item xs={12} md={4}>
                <SectionCard>
                  <CardContent>
                    <Typography variant="h6" color="#e4f1ff" fontWeight={600}>
                      消费榜
                    </Typography>
                    <Divider sx={{ my: 2, borderColor: 'rgba(0, 229, 255, 0.12)' }} />
                    {renderTopList(topUsers?.topConsumptionUsers, '近期暂无消费记录')}
                  </CardContent>
                </SectionCard>
              </Grid>
              <Grid item xs={12} md={4}>
                <SectionCard>
                  <CardContent>
                    <Typography variant="h6" color="#e4f1ff" fontWeight={600}>
                      余额榜
                    </Typography>
                    <Divider sx={{ my: 2, borderColor: 'rgba(0, 229, 255, 0.12)' }} />
                    {renderTopList(topUsers?.topBalanceUsers, '暂无余额数据')}
                  </CardContent>
                </SectionCard>
              </Grid>
            </Grid>

            <SectionCard>
              <CardContent>
                <Typography variant="h6" color="#e4f1ff" fontWeight={600}>
                  风险与提示
                </Typography>
                <Typography variant="caption" color="rgba(214,227,252,0.55)">
                  针对低余额、沉睡用户和关键指标波动的即时提醒
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <Alert
                      icon={<LocalAtmIcon fontSize="inherit" />}
                      severity="warning"
                      sx={{
                        borderRadius: 2,
                        '& .MuiAlert-icon': { color: '#ffb74d' }
                      }}
                    >
                      当前低余额用户 {overview.alerts.lowBalanceUsers} 人，建议
                      <strong> 定期推送充值提醒</strong> 或配置自动提醒规则。
                    </Alert>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Alert
                      icon={<WarningIcon fontSize="inherit" />}
                      severity="info"
                      sx={{
                        borderRadius: 2,
                        '& .MuiAlert-icon': { color: '#64b5f6' }
                      }}
                    >
                      近 90 日未活跃的用户有 {overview.alerts.dormantUsers} 人，可通过
                      <strong> 福利邮件或短信召回</strong> 再度唤醒。
                    </Alert>
                  </Grid>
                </Grid>
              </CardContent>
            </SectionCard>

            {/* 充值记录面板已移至顶部 */}
          </>
        )}
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardWrapper>
  );
};

export default AdminAnalyticsDashboard;

